import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { IVariant, UnleashClient } from 'unleash-proxy-client';

import { SentryLogService } from '@shared/common/services/sentry-log/sentry-log.service';
import { UNLEASH_CONFIG, UnleashConfig } from '../unleash.config';
import { UnleashContextField } from '../unleash.type';

@Injectable({
  providedIn: 'root'
})
export class UnleashService<T extends string = string> {
  private readonly unleash: UnleashClient;
  private readonly updateSubject = new Subject<void>();

  constructor(
    @Inject(UNLEASH_CONFIG) private readonly config: UnleashConfig,
    private readonly sentryLogService: SentryLogService
  ) {
    this.unleash = new UnleashClient(this.config);
  }

  get update$(): Observable<void> {
    return this.updateSubject.asObservable();
  }

  /**
   * Registers listeners for `ready`, `update` and `error` events and starts the background polling.
   *
   * @usageNotes
   * This is exposed as a friendly method in the unleash module loader (see `unleash.factory.ts`).
   * It waits for the `ready` event before working with the client.
   *
   * Why?
   * Before it's ready, the client might not report the correct feature states.
   * @see {@link https://docs.getunleash.io/reference/sdks/javascript-browser#step-3-let-the-client-synchronize| Unleash Docs - Client Sync}
   */
  protected initialize(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.unleash.on('ready', () => {
        resolve();
      });

      this.unleash.on('update', () => {
        this.updateSubject.next();
      });

      this.unleash.on('error', (error) => {
        this.logErrorToSentry(error);
        // resolve the client when an error occurs on init
        resolve();
      });

      this.unleash.start();
    });
  }

  /**
   * Checks the state of a specific feature flag.
   *
   * @param featureName The feature flag to check
   *
   * @usageNotes
   * NOTE: If feature flag relies on a context update, subscribe to the `update$` event to get the correct state.
   * ```
   * this.unleashService.setContextField('apaleoPropertyId', 'ABCDEFGH-1')
   * // subscribe to updates from the client
   * this.unleashService.update$.subscribe(() => {
   *    const isBreakfastEnabled = this.unleashService.isEnabled('breakfast')
   *    // serve breakfast (^_^)
   * })
   * ```
   * Alternatively, you can make use of the `isEnabledOnContextChange$` observable.
   */
  isEnabled(featureName: T): boolean {
    return this.unleash.isEnabled(featureName);
  }

  /**
   * Checks the state of a specific feature flag based on a context update.
   * It returns an Observable that emits the state of the specified feature flag.
   *
   * @param featureName The feature flag to check
   *
   * @usageNotes
   * NOTE: This function monitors the `update$` Subject,
   * and is useful only if a feature flag relies on context update.
   *
   * @example
   * ```
   * this.unleashService.setContextField('apaleoPropertyId', 'ABCDEFGH-1')
   * unleashService.isEnabled$('breakfast').subscribe(isEnabled => {
   *   if (isEnabled) {
   *     console.log('serve breakfast');
   *   } else {
   *     console.log('no breakfast');
   *   }
   * });
   * ```
   */
  isEnabledOnContextChange$(featureName: T): Observable<boolean> {
    return this.update$.pipe(
      startWith(this.isEnabled(featureName)),
      map(() => this.isEnabled(featureName))
    );
  }

  /**
   * Get the variant of an enabled feature that has variants.
   *
   * @param featureName The feature name to obtain variant
   *
   * NOTE: If feature flag relies on a context update, subscribe to the `update$` event to get the correct state.
   * ```
   * this.unleashService.setContextField('apaleoPropertyId', 'ABCDEFGH-1')
   * // subscribe to updates from the client
   * this.unleashService.update$.subscribe(() => {
   *    const variant = this.unleashService.getVariant('breakfast')
   *    // serve breakfast variant (^_^)
   * })
   * ```
   * Alternatively, you can make use of the `getVariantOnContextChange$` observable.
   */
  getVariant(featureName: T): IVariant {
    return this.unleash.getVariant(featureName);
  }

  /**
   * Returns an Observable that emits the variant of an enabled feature that has variants.
   *
   * @param featureName The feature flag to obtain variant
   *
   * @usageNotes
   * This function monitors the `update$` Subject,
   * and is useful only if a feature flag relies on context update.
   */
  getVariantOnContextChange$(featureName: T): Observable<IVariant> {
    return this.update$.pipe(map(() => this.getVariant(featureName)));
  }

  /**
   * Update a context field.
   *
   * @param field The context property to update
   * @param value The context value
   */
  setContextField(field: UnleashContextField, value: string) {
    this.unleash.setContextField(field, value);
  }

  /**
   * When unleash client fails to fetch feature toggles, it attempts to fetch them again.
   * These are not critical error and we don't want them logged to Sentry.
   * @param error The error object returned from the Unleash client error event
   */
  private shouldIgnoreSentryError(error: Error): boolean {
    const ignorePatterns = [/Unleash: unable to fetch feature toggles/i, /Failed to fetch/i, /Load failed/i];

    return ignorePatterns.some((pattern) => pattern.test(error.message || ''));
  }

  private logErrorToSentry(error: any) {
    if (this.shouldIgnoreSentryError(error)) return;
    this.sentryLogService.captureMessage({
      message: 'Unleash Error',
      severityLevel: 'fatal',
      event: 'unleash_client_error',
      error
    });
  }
}
